import { useEffect, useRef } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { SessionState } from '../types';

/** Воспроизводит трек на экране, стартуя в общий момент playAt. */
export const AudioPlayer = () => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const wasPlayingRef = useRef(false);
  const lastNonceRef = useRef(-1);
  const playTimerRef = useRef<number | null>(null);
  const deadlinePerfRef = useRef<number | null>(null);
  const sessionRef = useRef<SessionState>(useSessionStore.getState().session);
  const session = useSessionStore((state) => state.session);
  const reportPosition = useSessionStore((state) => state.reportPosition);
  const trackEnded = useSessionStore((state) => state.trackEnded);
  sessionRef.current = session;

  const trackFile = session.currentTrack?.file ?? null;
  const { isPlaying, audioNonce } = session;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let cancelled = false;
    const { currentTrack, playFrom, playAt, serverNow, isPlaying: playing } =
      sessionRef.current;

    const clearPlayTimer = () => {
      if (playTimerRef.current !== null) {
        window.clearTimeout(playTimerRef.current);
        playTimerRef.current = null;
      }
    };

    const handleEnded = () => {
      trackEnded().catch(() => undefined);
    };

    audio.addEventListener('ended', handleEnded);

    if (!currentTrack) {
      clearPlayTimer();
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      lastNonceRef.current = audioNonce;
      return () => {
        cancelled = true;
        clearPlayTimer();
        audio.removeEventListener('ended', handleEnded);
      };
    }

    const nextSrc = `/tracks/${currentTrack.file}`;
    const currentSrc = audio.getAttribute('src') || '';
    const needsLoad = !currentSrc.endsWith(currentTrack.file);
    const from = playFrom ?? currentTrack.startTime ?? 0;
    const nonceChanged = lastNonceRef.current !== audioNonce;

    const armPlay = () => {
      if (cancelled) return;
      clearPlayTimer();
      if (!sessionRef.current.isPlaying) {
        audio.pause();
        return;
      }
      const wait =
        deadlinePerfRef.current === null
          ? 0
          : Math.max(0, deadlinePerfRef.current - performance.now());
      playTimerRef.current = window.setTimeout(() => {
        playTimerRef.current = null;
        if (cancelled || !sessionRef.current.isPlaying) return;
        audio.play().catch((error) => {
          console.error('Ошибка воспроизведения:', error);
        });
      }, wait);
    };

    const seekThenArm = () => {
      if (cancelled) return;
      const onReady = () => {
        audio.removeEventListener('seeked', onReady);
        armPlay();
      };
      if (Number.isFinite(from) && Math.abs(audio.currentTime - from) > 0.05) {
        audio.addEventListener('seeked', onReady);
        audio.currentTime = from;
        return;
      }
      armPlay();
    };

    if (nonceChanged) {
      lastNonceRef.current = audioNonce;
      const leadMs =
        playAt != null && serverNow
          ? Math.max(0, playAt - serverNow)
          : playAt != null
            ? Math.max(0, playAt - Date.now())
            : 0;
      deadlinePerfRef.current = performance.now() + leadMs;

      if (needsLoad) {
        const onLoaded = () => {
          audio.removeEventListener('loadedmetadata', onLoaded);
          seekThenArm();
        };
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.src = nextSrc;
        audio.load();
      } else {
        seekThenArm();
      }
    } else if (!playing) {
      clearPlayTimer();
      audio.pause();
    }

    return () => {
      cancelled = true;
      clearPlayTimer();
      audio.removeEventListener('ended', handleEnded);
    };
  }, [trackFile, isPlaying, audioNonce, trackEnded]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (wasPlayingRef.current && !isPlaying && trackFile) {
      audio.pause();
      reportPosition(audio.currentTime).catch(() => undefined);
    }
    wasPlayingRef.current = isPlaying;
  }, [isPlaying, trackFile, reportPosition]);

  return <audio ref={audioRef} />;
};
